import { Icon } from '@toggle/design-system';
import { useIsomorphicLayoutEffect } from '@toggle/helpers/src/hooks/use-isomorphic-layout-effect/useIsomorphicLayoutEffect';
import React, { createRef, FC, MouseEvent, useRef, useState } from 'react';

import { Company } from '@/api/search/search-schema';

import { SingleSearchChip } from '../single-search-chip/SingleSearchChip';
import * as S from './SearchChips.styles';
import { findChip, hasRefs } from './utils/searchChipsUtils';

export type ChipType = 'weekday' | 'recently' | 'later';

export interface SearchChip {
  label: string;
  companies: Company[];
  type: ChipType;
}

export interface SearchChipsProps {
  isChipFocused: boolean;
  chips: SearchChip[];
  active: SearchChip;
  onClick: (chip: SearchChip) => void;
}

const MIN_THRESHOLD = 3;

export const SearchChips: FC<SearchChipsProps> = ({
  onClick,
  chips,
  active,
  isChipFocused,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const initialPosition = useRef({ scrolled: 0, clientX: 0 });
  const isMoving = useRef(false);
  const canMove = useRef(false);
  const enableClick = useRef(true);
  const [isRightChevronVisible, setIsRightChevronVisible] = useState(false);
  const [isLeftChevronVisible, setIsLeftChevronVisible] = useState(false);
  const chipRefs = chips.map(() => createRef<HTMLButtonElement>());

  useIsomorphicLayoutEffect(() => {
    const activeChipIdx = chips.findIndex(c => c === active);
    const chipRef = chipRefs[activeChipIdx].current;

    if (!chipRef || !ref.current) {
      return;
    }

    if (
      chipRef.offsetLeft + chipRef.clientWidth >=
      ref.current.scrollLeft + ref.current.clientWidth
    ) {
      chipRef.scrollIntoView({
        block: 'end',
        inline: 'center',
      });
    } else if (chipRef.offsetLeft <= ref.current.scrollLeft) {
      chipRef.scrollIntoView({
        block: 'end',
        inline: 'end',
      });
    }
  }, [active]);

  const onMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (!ref.current) {
      return;
    }

    initialPosition.current.clientX = e.clientX;
    initialPosition.current.scrolled = ref.current.scrollLeft || 0;
    canMove.current = true;
  };

  const onMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const x = initialPosition.current.clientX - e.clientX;
    if (!canMove.current || !ref.current || Math.abs(x) <= MIN_THRESHOLD) {
      return;
    }

    isMoving.current = true;
    ref.current.scrollTo(initialPosition.current.scrolled + x, 0);
    setIsLeftChevronVisible(false);
    setIsRightChevronVisible(false);
  };

  const onMouseUp = () => {
    if (!ref.current || !canMove.current) {
      return;
    }

    canMove.current = false;
    isMoving.current = false;

    const isScrolledToEnd =
      ref.current.scrollLeft + ref.current.clientWidth >=
      ref.current.scrollWidth;

    setIsLeftChevronVisible(ref.current.scrollLeft > 0);
    setIsRightChevronVisible(!isScrolledToEnd);
  };

  const onMouseEnter = () => {
    if (!ref.current) {
      return;
    }

    const isScrolledToEnd =
      ref.current.scrollLeft + ref.current.clientWidth >=
      ref.current.scrollWidth;

    setIsLeftChevronVisible(ref.current.scrollLeft > 0);
    setIsRightChevronVisible(!isScrolledToEnd);
  };

  const onChipMouseUp = () => {
    enableClick.current = !isMoving.current;
  };

  const onChipClick = (chip: SearchChip) => {
    if (enableClick.current) {
      onClick(chip);
    }
  };

  const onChevronClick = (direction: 'left' | 'right') => {
    if (!ref.current || !hasRefs(chipRefs)) {
      return;
    }

    let isRightVisible = direction === 'left',
      isLeftVisible = direction === 'right',
      left;
    const containerWidth = ref.current.clientWidth;
    const scrollLeft = ref.current.scrollLeft;
    const chip = findChip({ chipRefs, direction, containerWidth, scrollLeft });

    if (direction === 'right') {
      left = chip.current.offsetLeft;
      isRightVisible = left + containerWidth <= ref.current.scrollWidth;
    } else {
      const containerRect = ref.current.getBoundingClientRect();
      const pxToTheRight =
        containerRect.right - chip.current.getBoundingClientRect().right;
      left = scrollLeft - pxToTheRight;
      isLeftVisible = left > 0;
    }

    ref.current.scrollTo({
      left,
      behavior: 'smooth',
    });
    canMove.current = false;
    setIsLeftChevronVisible(isLeftVisible);
    setIsRightChevronVisible(isRightVisible);
  };

  return (
    <S.Container>
      <S.ChipsContainer
        ref={ref}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseUp}
        data-testid="chips-container"
      >
        {chips.map((chip, idx) => (
          <SingleSearchChip
            ref={chipRefs[idx]}
            label={chip.label}
            key={chip.label}
            onMouseUp={onChipMouseUp}
            onClick={() => onChipClick(chip)}
            isActive={chip === active}
            isFocused={chip === active && isChipFocused}
            disabled={!chip.companies.length}
          />
        ))}
      </S.ChipsContainer>
      {isLeftChevronVisible && (
        <S.Chevron
          isScrolledToEnd
          onClick={() => onChevronClick('left')}
          data-testid="chevron-left"
        >
          <Icon iconName={'ChevronBoldLeft'} size={16} />
        </S.Chevron>
      )}
      {isRightChevronVisible && (
        <S.Chevron
          isScrolledToEnd={false}
          onClick={() => onChevronClick('right')}
          data-testid="chevron-right"
        >
          <Icon iconName={'ChevronBoldRight'} size={16} />
        </S.Chevron>
      )}
    </S.Container>
  );
};
